import React, { useContext, useEffect, useState } from "react";
import TemplateElementWarningsService from "packs/services/convertflow/template_element_warnings_service";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import useReviews from "../reviews/useReviews";
import ReviewSettings from "../reviews/ReviewSettings";
import NewReview from "../reviews/NewReview";
import ConditionsBuilder from "../conditions/ConditionsBuilder";
import SidePanelLabeledSection from "../shared/SidePanelLabeledSection";

const ReviewRecommendationSettings = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const { Reviews } = useReviews();

  let website = contextData.objects.website;
  let cta = contextData.objects.cta;
  let variant = contextData.objects.variant;
  let review_recommendation = props.review_recommendation;
  let element = props.element;
  let existing_conditions = Object.values(variant.conditions).filter(
    (condition) =>
      condition.contactable_type == "ReviewRecommendation" &&
      condition.contactable_id == review_recommendation.id &&
      condition.toBeDeleted !== true
  );
  let warningVisible = false;

  let object_info = "Conditional Review Recommendation";

  const reviewIntegrationTypes = ReviewService.AVAILABLE_INTEGRATION_TYPES;
  let reviewsSourceOptions = [
    {
      value: "ConvertFlow",
      text: "ConvertFlow",
    },
  ];

  reviewIntegrationTypes.forEach((integrationType) => {
    const integrationObject = Object.values(website.integrations).find(
      (i) => i.integration_type == integrationType && i.status == true);

    let disabled = true;
    if (integrationObject) {
      disabled = false;
    }

    reviewsSourceOptions.push({
      value: integrationType,
      text: integrationType,
      disabled: disabled,
    });
  });

  if (existing_conditions.length == 0) {
    warningVisible = true;
  }

  if (review_recommendation.options["reviews-source"] && review_recommendation.options["reviews-source"] !== "ConvertFlow") {
    object_info = `${review_recommendation.options["reviews-source"]} Reviews`;

    if (review_recommendation.options["reviews-filters-keyword"]) {
      object_info += ` - ${review_recommendation.options["reviews-filters-keyword"]}`;
    }

    if (review_recommendation.options["reviews-filters-rating-condition"] && review_recommendation.options["reviews-filters-rating-value"]) {
      object_info += ` - Rating ${review_recommendation.options["reviews-filters-rating-condition"]} ${review_recommendation.options["reviews-filters-rating-value"]}`;
    }
  }

  const [availableReviews, setAvailableReviews] = useState([]);
  const [reviewRecommendationReviews, setReviewRecommendationReviews] = useState([]);

  useEffect(() => {
    const integrationType = review_recommendation.options["reviews-source"] || "ConvertFlow";
    const integrationObject = Object.values(website.integrations).find(
      (i) => i.integration_type == integrationType && i.status == true
    );
    const integrationService = new ReviewsIntegrationsService(integrationType, integrationObject, website.id);

    new Promise(async (resolve, reject) => {
      const reviewsResponse = await integrationService.getStoreReviews();
      setAvailableReviews(reviewsResponse);

      const reviewsIds = review_recommendation.options["reviews-ids"]?.split(",")?.filter(id => id) || [];
      const reviewRecommendationReviews = reviewsIds
        .map(id => reviewsResponse.find(review => review.id.toString() === id))
        .filter(review => review);
      setReviewRecommendationReviews(reviewRecommendationReviews);
    })
  }, [
    review_recommendation.options['reviews-source'],
    review_recommendation.options["reviews-ids"],
    review_recommendation.options["reviews-last-updated"],
    review_recommendation.options["reviews-sorting"],
    review_recommendation.options["reviews-sorting-operator"],
    review_recommendation.options["reviews-filters"],
    review_recommendation.options["reviews-filters-keyword"],
    review_recommendation.options["reviews-filters-rating-condition"],
    review_recommendation.options["reviews-filters-rating-value"],
    review_recommendation.options["reviews-filters-has-video"],
    review_recommendation.options["reviews-filters-date-condition"],
    review_recommendation.options["reviews-filters-date-value"]
  ]);

  return (
    <React.Fragment>
      <VariantBuilderSetting
        object={review_recommendation}
        setting_name="[modal_trigger]"
        setting_type="item_v2"
        object_type="review_recommendations"
        object_id={review_recommendation.id}
        title={
          review_recommendation.options["label"]
            ? review_recommendation.options["label"]
            : object_info
        }
        modalTitle={`Review Recommendation - ${
          review_recommendation.options["label"]
            ? review_recommendation.options["label"]
            : object_info
        }`}
        style={{ margin: "4px 0px" }}
        label_max_length={25}
        label_hover_max_length={14}
        panel_type="modal"
        side_panel={true}
        draggable="false"
        warning={
          warningVisible
            ? "Review filter is incomplete"
            : TemplateElementWarningsService.getReviewRecommendationRollupWarning(
                review_recommendation,
                variant
              )
        }
        warningBorder={false}
        label_icon="fas fa-star"
        duplicate_payload={{
          element_id: element.id,
        }}
        callback={() => {
          if (review_recommendation.options["reviews-source"] == "ConvertFlow") {
            Reviews.sortableStart(null, review_recommendation);
          }
        }}
      >
        <VariantBuilderSetting
          object={review_recommendation}
          setting_name="[options][label]"
          setting_type="text"
          object_type="review_recommendations"
          object_id={review_recommendation.id}
          label={`Label this review recommendation`}
          value={
            review_recommendation.options["label"]
              ? review_recommendation.options["label"]
              : ""
          }
          placeholder="Write label here"
          update_on_input_end={true}
        />

        <SidePanelLabeledSection label="If">
          <ConditionsBuilder
            cta={cta}
            variant={variant}
            website={website}
            contactable_type="ReviewRecommendation"
            contactable_id={review_recommendation.id}
            dark_mode={contextData.dark_mode}
            builder={true}
            hide_delete_button={true}
          />
        </SidePanelLabeledSection>

        <SidePanelLabeledSection label="Then">
              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-source]"
                setting_type="dropdown"
                label="Reviews Source"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={
                  review_recommendation.options["reviews-source"]
                    ? review_recommendation.options["reviews-source"]
                    : "ConvertFlow"
                }
                options={reviewsSourceOptions.sort((a, b) => a.disabled ? 1 : b.disabled ? -1 : 0)}
              />

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-filter-type]"
                setting_type="dropdown"
                label="Reviews Filter Type"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filter-type"]}
                options={[
                  { value: "", text: "Selection" },
                  { value: "filter", text: "Filter" },
                ]}
              />

              <VariantBuilderSetting
                object={review_recommendation}
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                label="Reviews"
                right_button_tag={!review_recommendation.options["reviews-source"] || review_recommendation.options["reviews-source"] == "ConvertFlow" ? {
                  text: "Manage →",
                  onClick: () => {
                    window.open(`/websites/${website.id}/reviews`, "_blank");
                  }
                } : {}}
                setting_name="[options][reviews-ids]"
                setting_type="select"
                value={review_recommendation.options["reviews-ids"]}
                condition={[undefined, "", "selection"].includes(review_recommendation.options["reviews-filter-type"])}
                options={availableReviews.map((review) => ({
                  value: review.id.toString(),
                  text: review.customer_name && review.title
                    ? `${review.customer_name} - ${review.title}`
                    : review.customer_name
                      ? review.customer_name
                      : review.title
                        ? review.title
                        : `Review ${review.id}`
                }))}
                multi_select={true}
                allow_empty={true}
              />

              {!review_recommendation.options["reviews-source"] || review_recommendation.options["reviews-source"] == "ConvertFlow" ? (
                <div className="col-xs-12">
                  <NewReview object={review_recommendation} object_type="review_recommendations" />
                </div>
              ) : null}

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews]"
                setting_type="html"
                condition={
                  (!review_recommendation.options["reviews-source"] || review_recommendation.options["reviews-source"] == "ConvertFlow") &&
                  review_recommendation.options["reviews-sorting"] == "position" &&
                  reviewRecommendationReviews.length > 0
                }
                style={{
                  padding: "0px",
                  margin: reviewRecommendationReviews.length == 0 ? "15px" : "initial",
                  maxWidth: reviewRecommendationReviews.length == 0 ? "90%" : "initial",
                }}
              >
                <div className="col-xs-12">
                  <div className="mb-[5px] mt-5 text-xl text-black dark:text-white">
                    Reviews:
                  </div>
                </div>

                <div
                  className="sortable-container col-xs-12 reviews-list"
                  data-sortable-name="reviews-list"
                  data-object_type="reviews"
                  data-object_id={review_recommendation.id}
                >
                  {Object.values(reviewRecommendationReviews)
                    .map((review, i) => {
                      return (
                        <React.Fragment key={"review-settings-" + review.id}>
                          <ReviewSettings
                            review={review}
                            review_recommendation={review_recommendation}
                            website={website}
                            {...props}
                          />
                        </React.Fragment>
                      );
                    })}
                </div>
              </VariantBuilderSetting>

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-filters]"
                setting_type="select"
                label="Filters"
                object_type="review_recommendations"
                show_reset={true}
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters"]}
                options={
                  [
                    {
                      value: "keyword",
                      text: "Keyword",
                    },
                    {
                      value: "rating",
                      text: "Rating",
                    },
                    {
                      value: "video",
                      text: "Video",
                    },
                    {
                      value: "date",
                      text: "Date",
                    }
                  ]
                }
                condition={review_recommendation.options["reviews-filter-type"] == "filter"}
                multi_select={true}
              />

              <VariantBuilderSetting
                object={review_recommendation}
                object_id={review_recommendation.id}
                setting_name="[options][reviews-sorting-operator]"
                setting_type="options_buttons"
                object_type="review_recommendations"
                label="Sorting Operator"
                options={
                  [
                    { value: "asc", title: "Ascending", height: 40 },
                    { value: "desc", title: "Descending", height: 40 },
                  ]
                }
                value={review_recommendation.options["reviews-sorting-operator"]}
                placeholder={"asc"}
              />

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-sorting]"
                setting_type="select"
                label="Sort"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-sorting"] ? review_recommendation.options["reviews-sorting"] : "date"}
                show_reset={true}
                options={
                  [
                    { value: "position", text: "Position" },
                    { value: "date", text: "Date" },
                    { value: "rating", text: "Rating" },
                    { value: "title", text: "Title" },
                  ]
                }
              />

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-filters-keyword]"
                setting_type="text"
                label="Keywords"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters-keyword"]}
                placeholder="good, excellent (comma-separated)"
                condition={
                  review_recommendation.options["reviews-filter-type"] == "filter" &&
                  review_recommendation.options["reviews-filters"]?.includes("keyword") == true
                }
              />

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-filters-rating-condition]"
                setting_type="dropdown"
                label="Rating Condition"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters-rating-condition"]}
                options={[
                  { value: "greater_than", text: "Greater than" },
                  { value: "less_than", text: "Less than" },
                  { value: "equal_to", text: "Equal to" },
                ]}
                condition={
                  review_recommendation.options["reviews-filter-type"] == "filter" &&
                  review_recommendation.options["reviews-filters"]?.includes("rating") == true
                }
              />

              <VariantBuilderSetting
                object={review_recommendation}
                setting_name="[options][reviews-filters-rating-value]"
                setting_type="text"
                label="Rating Value"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters-rating-value"]}
                placeholder="5"
                condition={
                  review_recommendation.options["reviews-filter-type"] == "filter" &&
                  review_recommendation.options["reviews-filters"]?.includes("rating") == true
                }
              />

              <VariantBuilderSetting
                object={review_recommendation}
                label="Has Video"
                setting_name="[options][reviews-filters-has-video]"
                setting_type="checkbox"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters-has-video"] ? review_recommendation.options["reviews-filters-has-video"] : "false"}
                condition={
                  review_recommendation.options["reviews-filter-type"] == "filter" &&
                  review_recommendation.options["reviews-filters"]?.includes("video") == true
                }
              />

              <VariantBuilderSetting
                object={review_recommendation}
                label="Date Condition"
                setting_name="[options][reviews-filters-date-condition]"
                setting_type="dropdown"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters-date-condition"]}
                options={[
                  { value: "greater_than", text: "Greater than" },
                  { value: "less_than", text: "Less than" },
                  { value: "equal_to", text: "Equal to" },
                ]}
                condition={
                  review_recommendation.options["reviews-filter-type"] == "filter" &&
                  review_recommendation.options["reviews-filters"]?.includes("date") == true
                }
              />

              <VariantBuilderSetting
                object={review_recommendation}
                label="Date Value"
                setting_name="[options][reviews-filters-date-value]"
                setting_type="date"
                object_type="review_recommendations"
                object_id={review_recommendation.id}
                value={review_recommendation.options["reviews-filters-date-value"]}
                placeholder="2021-01-01"
                condition={
                  review_recommendation.options["reviews-filter-type"] == "filter" &&
                  review_recommendation.options["reviews-filters"]?.includes("date") == true
                }
              />
        </SidePanelLabeledSection>
      </VariantBuilderSetting>
    </React.Fragment>
  );
};

export default ReviewRecommendationSettings;
